import React, { useCallback, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';
import { noop } from 'lodash';
import type { State as AppState } from 'state';
import { fetchPortalUploadMediaTokenAction } from 'state/actions/portal';
import { proformaFormDirtyAction } from 'state/actions/request-create';
import {
	getProjectId,
	getServiceDeskId,
	getMediaApiUploadToken,
	getMediaApiError,
} from 'state/selectors/portal';
import { getProformaTemplateForm, getIsProformaFormDirty } from 'state/selectors/request-create';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { MediaConfig } from '../common/types/media';
import { ProformaRequestCreate } from './proforma-request-create';

import type { OwnProps, StateProps, DispatchProps } from './types';

type Props = OwnProps & StateProps & DispatchProps;

const RequestCreateApp = ({
	// OwnProps
	blockFormSubmission,
	fieldErrors,
	knowledgeBase,
	locale,
	portalId,
	registerProformaForm,
	reporterAccountId,
	requestTypeId,
	// StateProps
	mediaApiError,
	mediaApiUploadToken,
	projectId,
	proformaFormIsDirty,
	proformaTemplateForm,
	serviceDeskId,
	// DispatchProps
	fetchPortalUploadMediaToken,
	setProformaFormDirtyAction,
	renderFormFinished: onFormRendererLifecycleStageRenderComplete,
}: Props) => {
	// Build media upload config
	const updateMediaUploadToken = useCallback(() => {
		fetchPortalUploadMediaToken(portalId);
	}, [portalId, fetchPortalUploadMediaToken]);

	const mediaUploadConfig: MediaConfig = useMemo(() => {
		if (!mediaApiUploadToken) {
			return {
				baseUrl: '',
				collection: '',
				clientId: '',
				token: '',
				updateToken: noop,
			};
		}

		return {
			baseUrl: mediaApiUploadToken.endpointUrl,
			collection: mediaApiUploadToken.targetCollection,
			clientId: mediaApiUploadToken.clientId,
			token: mediaApiUploadToken.token,
			updateToken: updateMediaUploadToken,
		};
	}, [mediaApiUploadToken, updateMediaUploadToken]);

	// Fetch media upload token on mount if not already loaded
	useEffect(() => {
		if (!mediaApiUploadToken && !mediaApiError) {
			updateMediaUploadToken();
		}
	}, [mediaApiUploadToken, mediaApiError, updateMediaUploadToken]);

	return (
		<Box testId="connected-proforma-request-create">
			<ProformaRequestCreate
				blockFormSubmission={blockFormSubmission}
				fieldErrors={fieldErrors}
				knowledgeBase={knowledgeBase}
				locale={locale}
				mediaUploadConfig={mediaUploadConfig}
				onRenderComplete={onFormRendererLifecycleStageRenderComplete}
				portalId={portalId}
				proformaFormIsDirty={proformaFormIsDirty}
				proformaTemplateForm={proformaTemplateForm}
				projectId={projectId}
				registerProformaForm={registerProformaForm}
				reporterAccountId={reporterAccountId}
				requestTypeId={requestTypeId}
				serviceDeskId={serviceDeskId}
				setProformaFormDirtyAction={setProformaFormDirtyAction}
			/>
		</Box>
	);
};

export const ConnectedProformaRequestCreate = connect<
	StateProps,
	DispatchProps,
	OwnProps,
	AppState
>(
	(state: AppState, { portalId, requestTypeId }: OwnProps) => ({
		mediaApiError: getMediaApiError(state, portalId),
		mediaApiUploadToken: getMediaApiUploadToken(state, portalId),
		projectId: getProjectId(state, portalId) as number,
		proformaFormIsDirty: getIsProformaFormDirty(state, portalId, requestTypeId),
		proformaTemplateForm: getProformaTemplateForm(state, portalId, requestTypeId),
		serviceDeskId: getServiceDeskId(state, portalId) as number,
	}),
	{
		fetchPortalUploadMediaToken: fetchPortalUploadMediaTokenAction,
		setProformaFormDirtyAction: proformaFormDirtyAction,
	},
)(RequestCreateApp);
